/* ============================================================================
 * battery-report.js — reads the battery report Windows writes about itself
 * (`powercfg /batteryreport`, the HTML file or the `/xml` variant) and says
 * what it shows. `window.BatteryReport`; no DOM, UMD, so the same file runs in
 * the page and in node.
 *
 * WHY THIS EXISTS. A web page cannot ask a battery how worn it is: the Battery
 * Status API carries a percentage and a plug state, nothing else. The numbers
 * that answer "is my battery dying" — design capacity, full-charge capacity,
 * cycle count, and the level at every power transition of the past week —
 * live in the operating system, and on Windows one command writes them to a
 * file. That file is where a report like "it sits at half and then drops to
 * zero" can actually be checked, so the page reads it instead of pointing at
 * it from a FAQ.
 *
 * WHAT THE FILE IS, MEASURED (101 HTML reports collected from public
 * repositories, 99 of them from machines with a battery, plus 12 XML ones;
 * Windows 8.1 → 11):
 *   - The HTML is English on every display language — the stylesheet is not
 *     localised. Only the NUMBER FORMAT follows the regional settings
 *     ("90 005 mWh", "45,030 mWh", "45.030 mWh"), so capacities are read by
 *     digits alone; they are whole milliwatt-hours, there is no fraction to
 *     lose.
 *   - Structure is carried by classes, and that is what the parser keys on:
 *     tr.dc = on battery, tr.suspend = asleep, td.dateTime / .state / .acdc /
 *     .percent / .mw. Sections are found by what they contain, not by
 *     position or heading.
 *   - A row is written only at a power TRANSITION (active ↔ standby ↔ sleep,
 *     plug ↔ unplug). Between two rows the level is not sampled. So a drop
 *     between two rows that are far apart proves nothing — it may be an hour of
 *     work — and a drop between two rows that are close proves a great deal.
 *     Every verdict below is about the second kind.
 *
 * THE VERDICTS, AND WHAT EACH RESTS ON.
 *   health     full-charge capacity ÷ design capacity. Both come from the
 *              battery's own gauge; Windows only copies them. ONE TRAP, in 14
 *              of the 99 files: the firmware (HP, mostly) writes the CURRENT
 *              capacity into the design field, week after week, so the ratio
 *              is 100 % on a pack that has lost half of itself. The history
 *              table still holds the larger figures, so for those machines
 *              health is measured against the largest capacity the report
 *              remembers — and labelled as such, because it is an upper bound.
 *   falls      a drop on battery faster than a pack in working order can be
 *              drained. Across the corpus 806 awake on-battery intervals lost
 *              ten points or more. For the 794 whose built capacity is known,
 *              the rate in whole batteries per hour is at most 1.21 for 791
 *              of them and at least 1.85 for the other three, with nothing in
 *              between; the three are the gauge correcting itself: it had been
 *              counting down a capacity the cells no longer hold, the voltage
 *              reached empty early, and the reading was snapped to match. That
 *              is "it sits at half and then drops to zero", on record (4 of
 *              the 99 machines, in one week of log each). The line is drawn in
 *              the empty gap (FALL_MIN_RATE). Where the built capacity is not
 *              known the rate is against the present one and the bar is twice
 *              as high (FALL_MIN_RATE_UNSURE): ordinary use reaches it only on
 *              a pack that has lost more than half of itself, which is the
 *              same news for the owner.
 *   relearns   the full-charge capacity moving by more than RELEARN_MIN_STEP
 *              of design between two periods of the history: the gauge
 *              re-estimating a pack it had lost track of.
 *
 * READINGS THAT ARE NOT A LEVEL, both met in the corpus: a sample above 105 %
 * (the gauge returned its design figure as the charge left) and a row with no
 * percentage at all. Neither starts, continues or ends a fall.
 *
 * WHAT A FALL IS NOT SAID TO BE. It is a statement about the READING: the
 * number moved in a way the cells cannot. In the corpus the reading sometimes
 * stays down (a pack that really is empty at "45 %") and sometimes is back at
 * 100 % a minute after the charger goes in (a gauge that lost its place). Both
 * are the same complaint from the owner's chair, and neither is told apart
 * here — a rule that discarded the second kind as "flicker" threw away the
 * clearest case in the whole set (80 % → 1 % in four minutes).
 *
 * WHAT IT DOES NOT CLAIM. No fall in the report is not "no fall happened":
 * a drop spread over a long interval is indistinguishable from use. analyze()
 * returns how much on-battery time the log holds so the page can say which of
 * the two it is looking at. With two packs installed nothing is judged at
 * all: attaching a discharged base to a charged tablet moves the combined
 * level just as abruptly, and the log does not say which pack a row is.
 * ==========================================================================*/
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.BatteryReport = factory();
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    /* Thresholds. FALL_MIN_RATE is read off the corpus distribution
       (tests/fixtures/battery-report/corpus-drain-intervals.json), and
       tests/js/battery-report.test.mjs fails if it is moved out of the gap it
       was placed in. */
    var T = {
        /* Points lost before a drop is examined at all: under this, one step
           of a coarse gauge plus a minute of load look the same. */
        FALL_MIN_DROP: 10,
        /* Whole batteries per hour, against the capacity the machine was built
           with. Use tops out at 1.21 in the corpus; gauge corrections start
           at 1.85. */
        FALL_MIN_RATE: 1.5,
        /* The same against the PRESENT capacity, for a report that does not
           know the built one and for a live percentage trace, which never
           does. Twice the bar above: use at the top of the normal range gets
           here only on a pack below half of what it was built with. */
        FALL_MIN_RATE_UNSURE: 3,
        /* A reading above this is not a level. */
        MAX_SANE_PCT: 105,
        /* Past this ratio the design figure is a placeholder. */
        MAX_SANE_HEALTH: 130,
        /* Points of design the full-charge figure moves between two
           consecutive history periods. */
        RELEARN_MIN_STEP: 8,
        /* …where "consecutive" means the next period really is the next one. */
        RELEARN_MAX_GAP_DAYS: 8,
        /* Health bands, percent of design. 80 is the line every large
           manufacturer uses for "consumed"; under 60 the missing capacity is
           larger than what a working day needs from what is left. */
        HEALTH_GOOD: 80,
        HEALTH_WORN: 60,
    };

    function fail(code) {
        var e = new Error('BatteryReport: ' + code);
        e.code = code;
        return e;
    }

    /* ── text helpers ─────────────────────────────────────────────────────── */

    var ENTITIES = { nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };

    function decode(s) {
        return s.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, function (m, ref) {
            if (ref.charAt(0) === '#') {
                var n = ref.charAt(1).toLowerCase() === 'x' ? parseInt(ref.slice(2), 16) : parseInt(ref.slice(1), 10);
                return isFinite(n) ? String.fromCharCode(n) : ' ';
            }
            var v = ENTITIES[ref.toLowerCase()];
            return v === undefined ? m : v;
        });
    }

    function text(fragment) {
        return decode(String(fragment).replace(/<[^>]*>/g, ' ')).replace(/[\s\u00a0\u202f]+/g, ' ').trim();
    }

    /* Whole number written in any regional format: grouping by space, comma,
       dot or apostrophe, and Arabic-Indic or Persian digits. Capacities and
       cycle counts are integers, so everything that is not a digit is
       grouping or a unit. */
    function int(s) {
        var d = String(s).replace(/[\u0660-\u0669]/g, function (c) { return String(c.charCodeAt(0) - 0x0660); })
                         .replace(/[\u06f0-\u06f9]/g, function (c) { return String(c.charCodeAt(0) - 0x06f0); })
                         .replace(/[^0-9]/g, '');
        return d ? parseInt(d, 10) : null;
    }

    /* The report's times are the machine's local wall clock with no zone. They
       are kept as that — milliseconds of a naive clock — because only the
       distances between them and their printed form are ever used. */
    function stamp(s) {
        var m = /(\d{4})-(\d{2})-(\d{2})(?:[T\s]+(\d{1,2}):(\d{2}):(\d{2}))?/.exec(String(s));
        if (!m) return null;
        return Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0));
    }

    function cls(attrs) {
        var m = /class\s*=\s*"([^"]*)"/i.exec(attrs || '');
        return m ? m[1].trim().split(/\s+/) : [];
    }

    function has(list, token) { return list.indexOf(token) !== -1; }

    /* ── HTML ─────────────────────────────────────────────────────────────── */

    function rows(html) {
        var out = [], re = /<tr\b([^>]*)>([\s\S]*?)<\/tr>/gi, m;
        while ((m = re.exec(html))) {
            var cells = [], cre = /<td\b([^>]*)>([\s\S]*?)<\/td>/gi, c;
            while ((c = cre.exec(m[2]))) cells.push({ cls: cls(c[1]), html: c[2] });
            out.push({ cls: cls(m[1]), cells: cells });
        }
        return out;
    }

    function cell(row, token) {
        for (var i = 0; i < row.cells.length; i++) if (has(row.cells[i].cls, token)) return row.cells[i];
        return null;
    }

    var LABELS = {
        'NAME': 'name', 'MANUFACTURER': 'manufacturer', 'SERIAL NUMBER': 'serial', 'CHEMISTRY': 'chemistry',
        'DESIGN CAPACITY': 'designMwh', 'FULL CHARGE CAPACITY': 'fullMwh', 'CYCLE COUNT': 'cycles',
    };
    /* The order the stylesheet has written these rows in since Windows 8, for a
       report whose labels are not the ones above. */
    var LABEL_ORDER = ['name', 'manufacturer', 'serial', 'chemistry', 'designMwh', 'fullMwh', 'cycles'];

    function htmlBatteries(part) {
        var labelled = rows(part).filter(function (r) {
            return r.cells.length >= 2 && /class\s*=\s*"label"/i.test(r.cells[0].html);
        });
        if (!labelled.length) return [];
        var known = labelled.filter(function (r) { return LABELS[text(r.cells[0].html).toUpperCase()]; }).length;
        var count = 0, i, k;
        for (i = 0; i < labelled.length; i++) count = Math.max(count, labelled[i].cells.length - 1);
        var list = [];
        for (k = 0; k < count; k++) list.push({ name: null, manufacturer: null, chemistry: null, designMwh: null, fullMwh: null, cycles: null });
        for (i = 0; i < labelled.length; i++) {
            var key = known ? LABELS[text(labelled[i].cells[0].html).toUpperCase()]
                            : (labelled.length === LABEL_ORDER.length ? LABEL_ORDER[i] : null);
            if (!key || key === 'serial') continue;
            for (k = 0; k < count; k++) {
                var c = labelled[i].cells[k + 1];
                if (!c) continue;
                var v = text(c.html);
                if (key === 'designMwh' || key === 'fullMwh' || key === 'cycles') list[k][key] = int(v);
                else list[k][key] = (v && v !== '-') ? v : null;
            }
        }
        return list;
    }

    function htmlUsage(part) {
        var out = [], day = null;
        rows(part).forEach(function (r) {
            var when = cell(r, 'dateTime');
            if (!when) return;
            var d = /class\s*=\s*"date"[^>]*>([\s\S]*?)<\/span>/i.exec(when.html);
            var t = /class\s*=\s*"time"[^>]*>([\s\S]*?)<\/span>/i.exec(when.html);
            /* The date is printed once per day and left blank on the rows under it. */
            var dayText = d ? text(d[1]) : '';
            if (dayText) day = dayText;
            if (!day || !t) return;
            var at = stamp(day + ' ' + text(t[1]));
            if (at == null) return;
            var stateCell = cell(r, 'state'), acdc = cell(r, 'acdc'), pct = cell(r, 'percent'), mw = cell(r, 'mw');
            var label = stateCell ? text(stateCell.html).toLowerCase() : '';
            var state = has(r.cls, 'suspend') ? 'suspend'
                      : /report/.test(label) ? 'report'
                      : /standby/.test(label) ? 'standby'
                      : 'active';
            out.push({
                t: at,
                state: state,
                /* A sleeping row carries no source in the HTML; the XML does. */
                ac: has(r.cls, 'dc') ? false : (acdc && text(acdc.html) ? true : null),
                pct: pct ? int(text(pct.html)) : null,
                mwh: mw ? int(text(mw.html)) : null,
            });
        });
        return out;
    }

    function htmlHistory(part) {
        var out = [];
        rows(part).forEach(function (r) {
            var when = cell(r, 'dateTime');
            if (!when) return;
            var dates = text(when.html).match(/\d{4}-\d{2}-\d{2}/g);
            if (!dates) return;
            var nums = r.cells.filter(function (c) { return has(c.cls, 'mw') || has(c.cls, 'nullValue'); })
                              .map(function (c) { return has(c.cls, 'mw') ? int(text(c.html)) : null; });
            if (nums.length < 2) return;
            out.push({ from: stamp(dates[0]), to: stamp(dates[dates.length - 1]), fullMwh: nums[0], designMwh: nums[1] });
        });
        return out;
    }

    function parseHtml(src) {
        var html = src.replace(/<style\b[\s\S]*?<\/style>/gi, '').replace(/<script\b[\s\S]*?<\/script>/gi, '');
        var parts = html.split(/<h2\b[^>]*>/i);
        var report = { format: 'html', batteries: [], usage: [], history: [] };
        for (var i = 1; i < parts.length; i++) {
            var p = parts[i];
            var acdc = /class\s*=\s*"acdc"/i.test(p), mw = /class\s*=\s*"mw"/i.test(p), hms = /class\s*=\s*"hms"/i.test(p);
            if (!report.batteries.length && /<span\s+class\s*=\s*"label"/i.test(p)) report.batteries = htmlBatteries(p);
            else if (acdc && !report.usage.length) report.usage = htmlUsage(p);
            else if (mw && !acdc && !hms && !report.history.length) report.history = htmlHistory(p);
        }
        return report;
    }

    /* ── XML ──────────────────────────────────────────────────────────────── */

    function attrs(tag) {
        var out = {}, re = /([A-Za-z_][\w.-]*)\s*=\s*"([^"]*)"/g, m;
        while ((m = re.exec(tag))) out[m[1]] = decode(m[2]);
        return out;
    }

    function child(block, name) {
        var m = new RegExp('<' + name + '\\b[^>]*>([\\s\\S]*?)</' + name + '>', 'i').exec(block);
        return m ? decode(m[1]).trim() : null;
    }

    var ENTRY_STATE = { active: 'active', connectedstandby: 'standby', suspend: 'suspend', reportgenerated: 'report' };

    function parseXml(src) {
        var report = { format: 'xml', batteries: [], usage: [], history: [] };
        var m, re;

        var installed = /<Batteries\b[^>]*>([\s\S]*?)<\/Batteries>/i.exec(src);
        re = /<Battery\b[^>]*>([\s\S]*?)<\/Battery>/gi;
        while (installed && (m = re.exec(installed[1]))) {
            report.batteries.push({
                name: child(m[1], 'Id') || null,
                manufacturer: child(m[1], 'Manufacturer') || null,
                chemistry: child(m[1], 'Chemistry') || null,
                designMwh: int(child(m[1], 'DesignCapacity') || ''),
                fullMwh: int(child(m[1], 'FullChargeCapacity') || ''),
                cycles: int(child(m[1], 'CycleCount') || ''),
            });
        }

        re = /<UsageEntry\b([^>]*?)\/?>/gi;
        while ((m = re.exec(src))) {
            var a = attrs(m[1]);
            var at = stamp(a.LocalTimestamp || a.Timestamp || '');
            if (at == null) continue;
            var mwh = int(a.ChargeCapacity || ''), full = int(a.FullChargeCapacity || '');
            report.usage.push({
                t: at,
                state: ENTRY_STATE[String(a.EntryType || '').toLowerCase()] || 'active',
                ac: a.Ac === '1' ? true : (a.Ac === '0' ? false : null),
                pct: (mwh != null && full) ? Math.round(mwh / full * 100) : null,
                mwh: mwh,
            });
        }

        re = /<HistoryEntry\b([^>]*?)\/?>/gi;
        while ((m = re.exec(src))) {
            var h = attrs(m[1]);
            var from = stamp(h.LocalStartDate || h.StartDate || ''), to = stamp(h.LocalEndDate || h.EndDate || '');
            if (from == null) continue;
            /* The HTML prints these periods by day; keep the two formats equal. */
            from -= from % 86400000;
            if (to != null) to -= to % 86400000;
            report.history.push({
                from: from, to: to == null ? from : to,
                fullMwh: int(h.FullChargeCapacity || '') || null,
                designMwh: int(h.DesignCapacity || '') || null,
            });
        }
        return report;
    }

    /* ── entry point ──────────────────────────────────────────────────────── */

    /** What kind of file this is, without parsing it: 'html' | 'xml' | null. */
    function sniff(src) {
        var head = String(src).slice(0, 4096);
        if (/<BatteryReport\b/i.test(head)) return 'xml';
        if (/schemas\.microsoft\.com\/battery\/2012/i.test(head) || /name\s*=\s*"ReportUtcOffset"/i.test(head)) return 'html';
        return null;
    }

    /**
     * @param {string} src  the file's text
     * @returns {{format, batteries[], usage[], history[]}}
     * @throws Error with .code: 'not_a_report' (some other file),
     *         'no_battery' (a desktop: the report exists and lists none)
     */
    function parse(src) {
        src = String(src == null ? '' : src).replace(/^\ufeff/, '');
        var kind = sniff(src);
        if (!kind) throw fail('not_a_report');
        var report = kind === 'xml' ? parseXml(src) : parseHtml(src);
        report.batteries = report.batteries.filter(function (b) {
            return b.designMwh || b.fullMwh || b.name;
        });
        if (!report.batteries.length) throw fail('no_battery');
        /* Stable: several rows share one second and their order is the story. */
        report.usage = report.usage.map(function (u, i) { return [u, i]; })
            .sort(function (p, q) { return p[0].t - q[0].t || p[1] - q[1]; })
            .map(function (p) { return p[0]; });
        report.history.sort(function (a, b) { return a.from - b.from; });
        return report;
    }

    /* ── analysis ─────────────────────────────────────────────────────────── */

    function sum(list, key) {
        var s = 0, any = false;
        list.forEach(function (b) { if (b[key] > 0) { s += b[key]; any = true; } });
        return any ? s : null;
    }

    function sane(row) {
        return row.pct != null && row.pct >= 0 && row.pct <= T.MAX_SANE_PCT;
    }

    /**
     * Drops that power draw cannot explain, as episodes. An episode is a chain
     * of consecutive readings in which EVERY step falls faster than the bar:
     * the fall is taken from where the level starts going down that fast to
     * where it stops. (Judging the chain by its overall rate instead lets an
     * hour of ordinary use lead into a fall and be counted as part of it — the
     * page said "52 % → 7 % in 5 min" for a level that stood at 50 % and went
     * to 7 % between two readings.) Works on a report's rows and on a live
     * trace alike.
     *
     * @param {Array<{t:number,pct:number,mwh?:number,ac?:boolean|null,state?:string}>} rows
     *        in time order; `t` in milliseconds
     * @param {{capacityMwh?:number, sure?:boolean}} [opt]
     *        capacityMwh — the capacity the rate is measured against; without
     *        it the rate comes from the percentages themselves.
     *        sure — that capacity is the one the machine was built with.
     */
    function falls(rows, opt) {
        opt = opt || {};
        var cap = opt.capacityMwh > 0 ? opt.capacityMwh : null;
        var bar = (cap && opt.sure) ? T.FALL_MIN_RATE : T.FALL_MIN_RATE_UNSURE;
        var out = [], i = 0, n = rows.length;

        function rate(a, b, hours) {
            if (cap && a.mwh != null && b.mwh != null) return (a.mwh - b.mwh) / cap / hours;
            return (a.pct - b.pct) / 100 / hours;
        }

        /* An episode starts on battery or asleep. A machine that is plugged in
           and awake is not discharging, whatever its reading does. */
        function startsEpisode(r) {
            return sane(r) && r.state !== 'report' && !(r.ac === true && r.state !== 'suspend');
        }

        /* One step of a chain. Several rows can share a second — a state change
           logged twice — and carry the chain through without moving it. */
        function fast(a, b) {
            if (!sane(b) || b.pct > a.pct) return false;
            var sec = (b.t - a.t) / 1000;
            if (!(sec > 0)) return sec === 0;
            return b.pct < a.pct && rate(a, b, sec / 3600) >= bar;
        }

        while (i < n - 1) {
            if (!startsEpisode(rows[i])) { i++; continue; }
            var j = i;
            while (j + 1 < n && fast(rows[j], rows[j + 1])) j++;
            /* Rows that only repeat the first or the last reading are not part
               of the fall's duration. */
            var from = i, to = j;
            while (from < to && rows[from + 1].pct === rows[from].pct && startsEpisode(rows[from + 1])) from++;
            while (to > from && rows[to - 1].pct === rows[to].pct) to--;
            var a = rows[from], e = rows[to], seconds = (e.t - a.t) / 1000;
            if (a.pct - e.pct >= T.FALL_MIN_DROP && seconds > 0) {
                out.push({
                    from: a.t, to: e.t, seconds: seconds,
                    fromPct: a.pct, toPct: e.pct, dropPct: a.pct - e.pct,
                    rate: rate(a, e, seconds / 3600),
                    watts: (a.mwh != null && e.mwh != null) ? (a.mwh - e.mwh) / 1000 / (seconds / 3600) : null,
                });
                i = j;
            } else {
                i++;
            }
        }
        return out;
    }

    /* The firmware writes the current capacity into the design field: the two
       are equal in nearly every period AND the "design" keeps changing. A new
       pack that reports exactly its design figure has the first property and
       not the second. */
    function mirrored(report, design, full) {
        if (design !== full) return false;
        var h = report.history.filter(function (x) { return x.fullMwh && x.designMwh; });
        if (h.length < 3) return false;
        var equal = 0, seen = {};
        h.forEach(function (x) { if (x.fullMwh === x.designMwh) equal++; seen[x.designMwh] = 1; });
        return equal / h.length >= 0.8 && Object.keys(seen).length >= 3;
    }

    /* The largest capacity the history holds in more than one period — one
       period alone can be a single bad reading. */
    function sustainedPeak(report) {
        var v = report.history.map(function (x) { return x.fullMwh || 0; }).filter(function (x) { return x > 0; });
        v.sort(function (p, q) { return q - p; });
        return v.length > 1 ? v[1] : (v[0] || null);
    }

    /* Time between rows that begin on battery and awake: how much there is in
       the log to judge a fall from. */
    function onBatterySeconds(rows) {
        var s = 0, i;
        for (i = 0; i + 1 < rows.length; i++) {
            if (rows[i].ac === false && rows[i].state !== 'suspend' && rows[i].state !== 'report') {
                s += Math.max(0, (rows[i + 1].t - rows[i].t) / 1000);
            }
        }
        return s;
    }

    /**
     * @param {object} report  from parse()
     * @returns {object}
     *   healthPct, band ('good' | 'aged' | 'worn' | null)
     *   reference {mwh, basis}: what health is measured against — 'design'
     *       (the reported figure), 'history' (mirroring firmware: the largest
     *       capacity the report remembers; health is then an upper bound),
     *       or null when the figures cannot be compared
     *   falls[], relearns[], history[] and the spans they were read from
     */
    function analyze(report) {
        var design = sum(report.batteries, 'designMwh'), full = sum(report.batteries, 'fullMwh');
        var cycles = null;
        /* The XML writes 0 where the HTML writes a dash: not reported. */
        report.batteries.forEach(function (b) { if (b.cycles > 0 && (cycles == null || b.cycles > cycles)) cycles = b.cycles; });

        var mirror = !!(design && full && mirrored(report, design, full));
        var reference = null;
        if (mirror) {
            var peak = sustainedPeak(report);
            if (peak) reference = { mwh: Math.max(peak, full), basis: 'history' };
        } else if (design && full && full / design * 100 <= T.MAX_SANE_HEALTH) {
            reference = { mwh: design, basis: 'design' };
        }

        var health = (reference && full) ? full / reference.mwh * 100 : null;
        var band = health == null ? null
                 : health >= T.HEALTH_GOOD ? 'good'
                 : health >= T.HEALTH_WORN ? 'aged'
                 : 'worn';

        var series = [];
        report.history.forEach(function (h) {
            if (!h.fullMwh) return;
            var against = mirror ? (reference && reference.mwh) : h.designMwh;
            if (!against) return;
            var pct = h.fullMwh / against * 100;
            if (pct > T.MAX_SANE_HEALTH) return;
            series.push({ t: h.from, to: h.to, fullMwh: h.fullMwh, designMwh: h.designMwh, pct: pct });
        });

        var relearns = [], i;
        for (i = 1; i < series.length; i++) {
            /* A swapped pack changes the design figure; that is a different
               battery, not this one changing its mind. Under mirroring the
               design figure is the capacity itself and says nothing. */
            if (!mirror && series[i].designMwh !== series[i - 1].designMwh) continue;
            /* The history skips the weeks the machine was not used, and half a
               year of wear between two rows is not a re-estimate. */
            if (series[i].t - series[i - 1].to > T.RELEARN_MAX_GAP_DAYS * 86400000) continue;
            if (Math.abs(series[i].pct - series[i - 1].pct) >= T.RELEARN_MIN_STEP) {
                relearns.push({ t: series[i].t, fromPct: series[i - 1].pct, toPct: series[i].pct });
            }
        }

        /* The capacity a rate is measured against. A reported design that
           differs from the full-charge figure is a real one; an equal pair
           that is not proven mirroring may be either, so it is not trusted. */
        var built = reference ? Math.max(reference.mwh, full || 0) : (full || null);
        var sure = !!reference && (reference.basis === 'history' || design !== full);

        var first = report.usage.length ? report.usage[0].t : null;
        var last = report.usage.length ? report.usage[report.usage.length - 1].t : null;
        var single = report.batteries.length === 1;

        return {
            designMwh: design, fullMwh: full, cycles: cycles,
            batteries: report.batteries.length,
            reference: reference, mirrored: mirror,
            /* What a discharge rate is measured against, and whether that is
               the capacity the machine was built with. */
            capacity: built ? { mwh: built, sure: sure } : null,
            healthPct: health, band: band,
            history: series, relearns: relearns,
            fallsJudged: !!(built && single),
            falls: (built && single) ? falls(report.usage, { capacityMwh: built, sure: sure }) : [],
            onBatterySeconds: onBatterySeconds(report.usage),
            span: (first != null && last != null) ? { from: first, to: last } : null,
        };
    }

    return { parse: parse, analyze: analyze, falls: falls, sniff: sniff, THRESHOLDS: T };
}));
